/**
 * AWG below-the-fold refresh — one small deferred script for the whole page.
 * No framework, no dependency.
 *
 * Responsibilities:
 *   1. Video facade — swap the poster for a real <video> on first activation.
 *   2. CTA proxy — [data-awg-atc] click-proxies to the live buy-box button.
 *   3. Live price binding for the mid-page strip and the closing CTA label.
 *   4. Judge.me review-header hydration (histogram, photo strip, filter chips).
 *   5. Sticky-bar rating sub-line + the html[data-awg-belowfold] scoping flag.
 *
 * The CTAs never submit their own form and never build a cart payload. They
 * click the real button, so variant selection, bundle pricing, sold-out state,
 * the cart drawer and every existing GA4 / Meta add_to_cart
 * event fire exactly once, from their existing code path.
 */
(function () {
  'use strict';

  if (window.__awgBelowFoldInit) return;
  window.__awgBelowFoldInit = true;

  document.documentElement.dataset.awgBelowfold = '';

  var JUDGEME_TIMEOUT_MS = 4000;

  /* ---------------------------------------------------------------- utils */

  function track(name, params) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, params || {}));
      } else if (typeof window.gtag === 'function') {
        window.gtag('event', name, params || {});
      }
    } catch {
      /* Analytics must never break a CTA. */
    }
  }

  function poll(resolveFn, timeoutMs, onFound, onTimeout) {
    var started = Date.now();
    (function attempt() {
      var found = resolveFn();
      if (found) {
        onFound(found);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        if (onTimeout) onTimeout();
        return;
      }
      window.setTimeout(attempt, 200);
    })();
  }

  /* -------------------------------------------------------- buy-box proxy */

  // Same fallback chain assets/sticky-add-to-cart.js already uses.
  function resolveBuyButton() {
    return (
      document.querySelector('main product-form button[name="add"]') ||
      document.querySelector('product-form .product-form__submit') ||
      document.querySelector('main .product-form__submit') ||
      document.querySelector('.product-form__submit')
    );
  }

  function resolveBuyForm() {
    return document.querySelector('main product-form form') || document.querySelector('product-form form');
  }

  function isBuyButtonDisabled(button) {
    if (!button) return true;
    return button.disabled === true || button.getAttribute('aria-disabled') === 'true';
  }

  function scrollToBuyBox() {
    var target = resolveBuyForm() || document.querySelector('#MainContent');
    if (!target) return;
    target.scrollIntoView({ block: 'center' });
    var picker =
      document.querySelector('.product-form__input input:not([type="hidden"])') ||
      document.querySelector('.product-form__input select') ||
      resolveBuyButton();
    if (picker && typeof picker.focus === 'function') picker.focus();
  }

  function readLivePriceText() {
    // Scoped to the buy box so a cross-sell price can never be picked up.
    var node =
      document.querySelector('main .price__sale .price-item--sale') ||
      document.querySelector('main .price__regular .price-item--regular') ||
      document.querySelector('main product-form .price .price-item, main .product__info-container .price .price-item');
    return node ? node.textContent.trim() : '';
  }

  function syncPrices() {
    var priceText = readLivePriceText();
    if (!priceText) return;

    document.querySelectorAll('[data-awg-price]').forEach(function (node) {
      node.textContent = priceText;
    });

    document.querySelectorAll('[data-awg-cta-template]').forEach(function (button) {
      var template = button.getAttribute('data-awg-cta-template');
      if (template && template.indexOf('{{ price }}') !== -1) {
        button.textContent = template.replace('{{ price }}', priceText);
      }
    });
  }

  function syncProxyDisabledState() {
    var disabled = isBuyButtonDisabled(resolveBuyButton());
    document.querySelectorAll('[data-awg-atc]').forEach(function (cta) {
      cta.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    });
  }

  function setupCtaProxies() {
    var ctas = document.querySelectorAll('[data-awg-atc]');
    if (!ctas.length) return;

    ctas.forEach(function (cta) {
      cta.addEventListener('click', function (event) {
        event.preventDefault();

        var button = resolveBuyButton();

        track('awg_belowfold_cta_click', {
          cta_location: cta.getAttribute('data-awg-atc'),
          product_id: cta.getAttribute('data-product-id') || '',
          variant_id: readSelectedVariantId(),
          value: readLivePriceText(),
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || ''
        });

        if (!button || isBuyButtonDisabled(button)) {
          // Never no-op: fall back to the buy box so the shopper can choose a
          // variant or read the sold-out state for themselves.
          scrollToBuyBox();
          return;
        }

        window._wrAtcSource = 'belowfold_' + cta.getAttribute('data-awg-atc');
        button.click();
      });
    });

    syncProxyDisabledState();
    syncPrices();

    document.addEventListener('variant:change', function () {
      syncPrices();
      syncProxyDisabledState();
    }, { passive: true });

    document.addEventListener('cart:updated', function () {
      syncProxyDisabledState();
    }, { passive: true });

    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, function () {
        syncPrices();
        syncProxyDisabledState();
      });
    }

    document.querySelectorAll('input[name="id"]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        window.requestAnimationFrame(function () {
          syncPrices();
          syncProxyDisabledState();
        });
      }, { passive: true });
    });
  }

  function readSelectedVariantId() {
    var input = document.querySelector('main product-form input[name="id"]') ||
                document.querySelector('product-form input[name="id"]');
    return input ? input.value : '';
  }

  /* --------------------------------------------------------- video facade */

  function setupVideoFacade() {
    var button = document.querySelector('[data-awg-play]');
    if (!button) return;

    button.addEventListener('click', function () {
      var player = button.closest('[data-awg-player]');
      if (!player) return;

      var videoSrc = button.getAttribute('data-video-src');
      var captionsSrc = button.getAttribute('data-captions-src');
      if (!videoSrc || !captionsSrc) return;

      var video = document.createElement('video');
      video.setAttribute('controls', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('preload', 'none');
      video.setAttribute('crossorigin', 'anonymous');
      video.setAttribute('width', '1200');
      video.setAttribute('height', '675');

      var posterSrc = button.getAttribute('data-poster-src');
      if (posterSrc) video.setAttribute('poster', posterSrc);

      var source = document.createElement('source');
      source.src = videoSrc;
      source.type = 'video/mp4';
      video.appendChild(source);

      var trackEl = document.createElement('track');
      trackEl.setAttribute('kind', 'captions');
      trackEl.setAttribute('src', captionsSrc);
      trackEl.setAttribute('srclang', 'en');
      trackEl.setAttribute('label', 'English');
      trackEl.setAttribute('default', '');
      video.appendChild(trackEl);

      var poster = player.querySelector('.player__poster');
      if (poster) poster.remove();
      button.remove();
      player.appendChild(video);

      track('awg_field_video_play', {
        video_id: 'awg-field-video',
        product_id: button.getAttribute('data-product-id') || ''
      });

      var played = video.play();
      if (played && typeof played.catch === 'function') {
        played.catch(function () {
          /* Autoplay-after-gesture can still be refused; native controls remain. */
        });
      }
      video.focus();
    });
  }

  /* ---------------------------------------------------- sticky bar sub-line */

  function setupStickyBarSubLine() {
    var payload = document.querySelector('[data-awg-stickybar-sub]');
    if (!payload) return;
    var text = payload.getAttribute('data-awg-stickybar-sub');
    if (!text) return;

    poll(
      function () {
        return document.querySelector('.sticky-add-to-cart__price-container');
      },
      JUDGEME_TIMEOUT_MS,
      function (container) {
        if (container.parentNode.querySelector('.awg-stickybar__sub')) return;
        var sub = document.createElement('span');
        sub.className = 'awg-stickybar__sub';
        sub.textContent = text;
        container.parentNode.appendChild(sub);
      }
    );
  }

  /* ------------------------------------------------- Judge.me review header */

  function hydrateHistogram(widget) {
    var container = document.querySelector('[data-awg-histogram]');
    if (!container) return;

    var rows = widget.querySelectorAll('.jdgm-histogram__row');
    if (!rows.length) {
      container.remove();
      return;
    }

    var counts = {};
    var total = 0;

    rows.forEach(function (row) {
      var rating = row.getAttribute('data-rating');
      var freqNode = row.querySelector('.jdgm-histogram__frequency');
      var count = freqNode ? parseInt(freqNode.textContent.replace(/[^0-9]/g, ''), 10) : NaN;
      if (!rating || isNaN(count)) return;
      counts[rating] = count;
      total += count;
    });

    if (!total) {
      container.remove();
      return;
    }

    container.querySelectorAll('[data-awg-bar]').forEach(function (bar) {
      var value = bar.getAttribute('data-awg-bar');
      var count = counts[value] || 0;
      var pct = Math.round((count / total) * 100);
      var fill = bar.querySelector('.bar__fill');
      var label = bar.querySelector('.bar__pct');
      if (fill) fill.style.width = pct + '%';
      if (label) label.textContent = String(count);
    });
  }

  function hydratePhotoStrip(widget) {
    var strip = document.querySelector('[data-awg-photostrip]');
    if (!strip) return;

    var images = widget.querySelectorAll('.jdgm-rev__pics img');
    if (images.length < 4) return; // Keep the curated fallback tiles.

    var fragment = document.createDocumentFragment();
    var sources = Array.prototype.slice.call(images, 0, 12);
    var total = sources.length;
    sources.forEach(function (source, index) {
      var src = source.currentSrc || source.getAttribute('src') || source.getAttribute('data-src');
      if (!src) return;
      var item = document.createElement('li');
      var img = document.createElement('img');
      img.src = src;
      img.width = 132;
      img.height = 132;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.alt = source.alt || 'Customer photo submitted with a review';
      // A real button so the tile is focusable, keyboard-operable and announced
      // as a control; the click routes into Judge.me's own full-size viewer.
      var open = document.createElement('button');
      open.type = 'button';
      open.className = 'photostrip__open';
      open.setAttribute('data-awg-photo-key', photoKey(src));
      open.setAttribute('data-awg-photo-src', src.split('?')[0]);
      open.setAttribute(
        'aria-label',
        'View full size: ' + img.alt + ' (' + (index + 1) + ' of ' + total + ')'
      );
      open.appendChild(img);
      item.appendChild(open);
      fragment.appendChild(item);
    });

    if (!fragment.childNodes.length) return;
    strip.textContent = '';
    strip.appendChild(fragment);
    strip.addEventListener('click', openPhotoInJudgeMeViewer);
    strip.addEventListener('focusin', keepFocusedTileInView);
  }

  // Chrome does not scroll a horizontal strip when Tab lands on a tile that is
  // only partly visible, which clips its focus ring. Scroll the strip itself
  // (horizontally only, so the page never jumps) far enough to show the whole
  // tile plus the strip's scroll-padding. Keyboard focus only: a mouse click on
  // an edge tile should not shift the strip under the pointer.
  function keepFocusedTileInView(event) {
    var tile = event.target.closest ? event.target.closest('.photostrip__open') : null;
    if (!tile) return;
    try {
      if (!tile.matches(':focus-visible')) return;
    } catch (e) {
      /* Browsers without :focus-visible: treat every focus as keyboard focus. */
    }
    var strip = event.currentTarget;
    var style = window.getComputedStyle(strip);
    var padStart = parseFloat(style.scrollPaddingInlineStart) || 0;
    var padEnd = parseFloat(style.scrollPaddingInlineEnd) || 0;
    var stripBox = strip.getBoundingClientRect();
    var tileBox = tile.getBoundingClientRect();
    var overflowEnd = tileBox.right - (stripBox.right - padEnd);
    var overflowStart = stripBox.left + padStart - tileBox.left;
    if (overflowEnd > 0) {
      strip.scrollLeft += Math.ceil(overflowEnd);
    } else if (overflowStart > 0) {
      strip.scrollLeft -= Math.ceil(overflowStart);
    }
  }

  // Judge.me serves the same review image at different sizes via the query
  // string, so host + path alone identifies the photo (protocol-relative and
  // fragment variants normalise to the same key).
  function photoKey(url) {
    try {
      var parsed = new URL(url, window.location.href);
      return parsed.host + parsed.pathname;
    } catch (e) {
      return String(url || '').split(/[?#]/)[0];
    }
  }

  // The widget's own media grid (.jm-media-preview) is hidden on this template
  // by awg-judgeme-restyle.css, but its buttons stay in the DOM with Judge.me's
  // handlers attached. Activating the matching one opens Judge.me's viewer at
  // that photo — no second lightbox is built here.
  function openPhotoInJudgeMeViewer(event) {
    var open = event.target.closest('.photostrip__open');
    if (!open) return;
    var key = open.getAttribute('data-awg-photo-key');
    var fullSize = open.getAttribute('data-awg-photo-src');
    var items = document.querySelectorAll('.jm-media-grid__item');
    for (var i = 0; i < items.length; i++) {
      var thumb = items[i].querySelector('img');
      if (thumb && photoKey(thumb.getAttribute('src')) === key) {
        items[i].click();
        return;
      }
    }
    // No matching Judge.me grid item: open the full-size file instead of
    // leaving a dead click.
    if (fullSize) window.open(fullSize, '_blank', 'noopener');
  }

  // Each chip must resolve a real Judge.me control or it does not render.
  // Dead chips are a QA failure.
  function resolveChipControl(kind, widget) {
    if (kind === 'all') {
      return (
        widget.querySelector('.jdgm-histogram__row.jdgm-histogram__row--active') ||
        widget.querySelector('.jdgm-histogram__row[aria-pressed="true"]') ||
        widget.querySelector('.jdgm-rev-widg__clear-filters')
      );
    }
    if (kind === '5_star') {
      return widget.querySelector('.jdgm-histogram__row[data-rating="5"]');
    }
    if (kind === 'critical') {
      return (
        widget.querySelector('.jdgm-histogram__row[data-rating="1"]') &&
        widget.querySelector('.jdgm-sort-dropdown, .jdgm-rev-widg__sort-wrapper select')
      );
    }
    if (kind === 'with_photos') {
      return (
        widget.querySelector('[data-jdgm-filter="pictures"]') ||
        widget.querySelector('.jdgm-pictures-filter') ||
        widget.querySelector('.jdgm-sort-dropdown option[value*="picture"]')
      );
    }
    if (kind === 'keyword') {
      return (
        widget.querySelector('.jdgm-search-rev__input') ||
        widget.querySelector('input.jdgm-rev-widg__search-input') ||
        widget.querySelector('.jdgm-rev-widg__search input')
      );
    }
    return null;
  }

  function applyChip(chip, widget) {
    var kind = chip.getAttribute('data-awg-chip');

    if (kind === 'all') {
      var active = widget.querySelector('.jdgm-histogram__row.jdgm-histogram__row--active');
      if (active) {
        active.click();
      } else {
        var clear = widget.querySelector('.jdgm-rev-widg__clear-filters');
        if (clear) clear.click();
      }
    } else if (kind === '5_star') {
      var five = widget.querySelector('.jdgm-histogram__row[data-rating="5"]');
      if (five) five.click();
    } else if (kind === 'critical') {
      var sort = widget.querySelector('.jdgm-sort-dropdown, .jdgm-rev-widg__sort-wrapper select');
      if (sort) {
        sort.value = 'rating-low-to-high';
        sort.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (kind === 'with_photos') {
      var pics =
        widget.querySelector('[data-jdgm-filter="pictures"]') || widget.querySelector('.jdgm-pictures-filter');
      if (pics) {
        pics.click();
      } else {
        var sortPics = widget.querySelector('.jdgm-sort-dropdown');
        if (sortPics) {
          sortPics.value = 'pictures-first';
          sortPics.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    } else if (kind === 'keyword') {
      var input = resolveChipControl('keyword', widget);
      if (input) {
        input.value = chip.getAttribute('data-awg-keyword') || '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    document.querySelectorAll('[data-awg-chip]').forEach(function (other) {
      other.setAttribute('aria-pressed', other === chip ? 'true' : 'false');
    });

    var status = document.querySelector('[data-awg-chip-status]');
    if (status) status.textContent = 'Reviews filtered by ' + chip.textContent.trim() + '.';
  }

  function hydrateChips(widget) {
    var group = document.querySelector('[data-awg-chips]');
    if (!group) return;

    var surviving = 0;
    group.querySelectorAll('[data-awg-chip]').forEach(function (chip) {
      var kind = chip.getAttribute('data-awg-chip');
      if (!resolveChipControl(kind, widget)) {
        chip.remove();
        return;
      }
      surviving += 1;
      chip.addEventListener('click', function () {
        applyChip(chip, widget);
      });
    });

    if (surviving < 2) {
      group.remove();
      return;
    }
    group.removeAttribute('hidden');
  }

  function setupReviewHeader() {
    if (!document.querySelector('[data-awg-histogram], [data-awg-chips], [data-awg-photostrip]')) return;

    poll(
      function () {
        var widget = document.querySelector('.jdgm-rev-widg, .jdgm-widget.jdgm-review-widget');
        return widget && widget.querySelector('.jdgm-histogram__row, .jdgm-rev') ? widget : null;
      },
      JUDGEME_TIMEOUT_MS,
      function (widget) {
        hydrateHistogram(widget);
        hydratePhotoStrip(widget);
        hydrateChips(widget);
      },
      function () {
        var bars = document.querySelector('[data-awg-histogram]');
        if (bars) bars.remove();
        var chips = document.querySelector('[data-awg-chips]');
        if (chips) chips.remove();
      }
    );
  }

  /* ------------------------------------------------- suit-diagram hotspots */

  // The legend stands alone with JS off; this only adds the shortcut from a
  // numbered marker on the garment to the matching legend entry.
  function setupDiagramHotspots() {
    var hotspots = document.querySelectorAll('[data-awg-hotspot]');
    if (!hotspots.length) return;

    Array.prototype.forEach.call(hotspots, function (hotspot) {
      hotspot.addEventListener('click', function () {
        var target = document.getElementById(hotspot.getAttribute('data-awg-hotspot'));
        if (!target) return;

        Array.prototype.forEach.call(hotspots, function (other) {
          other.removeAttribute('aria-current');
        });
        Array.prototype.forEach.call(
          document.querySelectorAll('.callout.is-active'),
          function (callout) {
            callout.classList.remove('is-active');
          }
        );

        hotspot.setAttribute('aria-current', 'true');
        target.classList.add('is-active');

        var still =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ block: 'nearest', behavior: still ? 'auto' : 'smooth' });
      });
    });
  }

  /* ------------------------------------------------------------------ boot */

  function init() {
    setupCtaProxies();
    setupVideoFacade();
    setupStickyBarSubLine();
    setupReviewHeader();
    setupDiagramHotspots();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
